import { memo } from 'react';
import { Icon } from 'semantic-ui-react';
import { useRowBoundingBox } from '../../../../../../stores/boundingBoxesStore';
import { ImportDescriptorIdType, ImportTableDescriptorType } from '../../../../../../types';
import { ImageHighlighterWrapper } from '../../../../../imageHighlighter/imageHighlighter';
import {
    useImportTableStoreDescriptorsEditMode,
    useImportTableStoreDescriptor,
    useImportTableStoreDescriptorActions,
    useImportTableStoreReportsFieldValue,
} from '../../../../context/importTableContext';
import { getDescriptorCellStyling } from './utils';
import { FIELD_VALUE_SOURCE, IMPORT_DESCRIPTOR_SOURCE } from '../../../../../../constants/generalConstants';
import ObjCheck from '../../../../../../../../../utils/objCheck';

type DescriptorLabelCellPropsType = {
    descriptorId: ImportDescriptorIdType;
};
const DescriptorLabelCell = memo(function DescriptorCell({ descriptorId }: DescriptorLabelCellPropsType) {
    const descriptor = useImportTableStoreDescriptor(descriptorId);
    const editing = useImportTableStoreDescriptorsEditMode();
    const { updateDescriptorLabel, enableDescriptorsEditMode } = useImportTableStoreDescriptorActions();
    const rowBoundingBox = useRowBoundingBox(descriptor.id);
    const fieldValues = useImportTableStoreReportsFieldValue(descriptorId);
    const importValues = fieldValues
        .filter((fieldValue) => !ObjCheck.isNullUndefinedOrEmpty(fieldValue.normalizedValue));
    const noImportValues = !importValues.length ? false : importValues
        .every((fieldValue) => fieldValue.source === FIELD_VALUE_SOURCE.MASTER_TABLE);
    const { styles, classes } = getDescriptorCellStyling(descriptor, editing);

    return (
        <td style={styles} className={classes} onDoubleClick={enableDescriptorsEditMode}>
            <ImageHighlighterWrapper boundingBox={rowBoundingBox}>
                {editing ? (
                    <EditDescriptorLabel
                        descriptor={descriptor}
                        handleChange={(data) => updateDescriptorLabel(descriptor.id, data)}
                        disabled={
                            descriptor.source === IMPORT_DESCRIPTOR_SOURCE.MASTER_TABLE ||
                            noImportValues
                        }
                    />
                ) : (
                    <ReadDescriptorLabel descriptor={descriptor} />
                )}
            </ImageHighlighterWrapper>
        </td>
    );
});

export default DescriptorLabelCell;

type EditDescriptorLabelPropsType = {
    descriptor: ImportTableDescriptorType;
    handleChange: (data: string) => void;
    disabled?: boolean;
};
function EditDescriptorLabel({ descriptor, handleChange, disabled }: EditDescriptorLabelPropsType) {
    return (
        <>
            <input
                disabled={disabled}
                className="cell-input outline--none border-w--1"
                style={{ width: 400 }}
                type="text"
                value={descriptor.label}
                onChange={(e) => handleChange(e.target.value)}
            />
        </>
    );
}

type ReadDescriptorLabelPropsType = {
    descriptor: ImportTableDescriptorType;
};
function ReadDescriptorLabel({ descriptor }: ReadDescriptorLabelPropsType) {
    return (
        <div className="flex flex-align-center flex-gap-1">
            <div className="flex flex-align-center">{descriptor.label}</div>
            {descriptor.newDescriptor && (
                <Icon className="flex flex-align-center flex-justify-center" color="yellow" name="plus" />
            )}
        </div>
    );
}
